<template>
    <v-container container--fluid>
        <v-layout v-if="!classesLoaded" row wrap class="pa-2 mx-auto full-height">
            <v-flex xs12 class="justify-center text-center mt-12">
                <span class="font-weight-bold title">Loading Data... <i class="fas fa-circle-notch fa-spin"></i></span>
            </v-flex>
        </v-layout>
        <v-layout v-if="classesLoaded" row wrap align-center justify-space-around class="pb-16 pt-8 px-3 mx-auto">
            <v-flex xs12 class="text-left">
                <v-row>
                    <v-col class="pa-1 mb-2 d-flex justify-start" cols="12">
                        <h1 class="grey--text lighten-3 align-self-center">Classes</h1>
                    </v-col>
                    <v-col class="pa-1 mb-2 d-flex justify-start legend-wrapper" cols="12">
                        <div v-if="classes_list.length > 0" v-for="event in getUniqueEvents()" :key="event.class_id">
                            <legend class="cl-legend cl-booked" :style="getUniqueEventStyle(event)">Available - {{ event.class_activity }}</legend>
                        </div>
                        <legend  v-if="classes_list.length == 0" class="cl-legend cl-booked">Available</legend>
                        <legend class="cl-legend cl-avail">Booked</legend>
                        <legend class="cl-legend cl-waitlist">Waitlist</legend>
                    </v-col>
                    <v-col class="pa-1 mb-2 d-flex justify-start" cols="12">
                        <p class="mb-0"> HPC members are eligible to make bookings up to <b>14 days in advance</b>. The schedule below outlines upcoming availability across the <b>next four weeks</b>, allowing you to plan accordingly.</p>
                    </v-col>
                    <v-col cols="12" sm="12" md="12" class="">
                        <div class="week-view">
                            <!-- Controls -->
                            <div class="controls new">
                                <button v-if="showPrev" @click="changeWeek(-1)">← Prev</button>
                                <strong>{{ visibleDays[0].format("MMM D") }} - {{ visibleDays[visibleDays.length - 1].format("MMM D") }}</strong>
                                <button v-if="showNext" @click="changeWeek(+1)">Next →</button>
                            </div>

                            <!-- Header -->
                            <div class="header-row">
                                <div class="time-column-header"></div>
                                <div class="day-column" v-for="day in visibleDays" :key="day.format('YYYY-MM-DD')">
                                    <div class="day-label">{{ day.format("ddd") }}</div>
                                    <div class="date-label">{{ day.format("MMM D") }}</div>
                                </div>
                            </div>

                            <!-- Grid layout for background cells -->
                            <div class="body-row" v-for="(time, tIndex) in timeSlots" :key="tIndex">
                                <div class="time-column">{{ time }}</div>
                                <div class="day-cell" v-for="day in visibleDays" :key="day.format('YYYY-MM-DD')" :style="{ height: `${slotHeight}px` }"></div>
                            </div>

                            <!-- Floating event layer -->
                            <div class="event-layer">
                                <div class="day-events" v-for="day in visibleDays" :key="day.format('YYYY-MM-DD')">
                                    <div class="event" :class="(event.class_booked ? 'event-booked' : (event.class_waitlist ? 'event-waitlist' : ''))" v-for="event in getEventsForDay(day)" :key="event.class_id" :style="getEventStyle(event)" @click.stop="showEventDetails(event)">
                                        <p>{{ event.class_name }}</p>
                                    </div>
                                </div>
                            </div>

                            <!-- Modal -->
                            <div class="modal" v-if="selectedEvent">
                                <div class="modal-content">
                                    <span class="close" @click="selectedEvent = null">&times;</span>
                                    <h3 class="mb-4">{{ selectedEvent.class_name }}</h3>
                                    <v-row class="mt-0 mb-2">
                                        <v-col cols="12" sm="12" md="12" class="">
                                            <p class="ma-0"><strong>Description:</strong> {{ selectedEvent.class_description }}</p>
                                        </v-col>
                                    </v-row>
                                    <v-row class="mt-0 mb-2">
                                        <v-col cols="12" sm="6" md="6" class="">
                                            <p class="ma-0 text-capitalize">
                                                <strong>Date:</strong> {{ selectedEvent.class_date }}<br>
                                                <strong>Time:</strong> {{ selectedEvent.class_starts_at }} - {{ selectedEvent.class_ends_at }} ({{ selectedEvent.duration }} mins)<br>
                                                <strong>Venue:</strong> {{ selectedEvent.class_venue }}
                                                <span v-if="selectedEvent.class_room"><br><strong>Room:</strong> {{ selectedEvent.class_room }}</span>
                                            </p>
                                        </v-col>
                                        <v-col cols="12" sm="6" md="6" class="">
                                            <p class="ma-0 text-capitalize">
                                                <strong>Trainer:</strong> {{ selectedEvent.class_trainers.replaceAll(',', ', ') }}<br>
                                                <strong>Activity:</strong> {{ selectedEvent.class_activity.replaceAll(',', ', ') }}<br>
                                                <strong>Capacity:</strong> {{ selectedEvent.class_capacity }} spots remaining
                                            </p>
                                        </v-col>
                                    </v-row>

                                    <v-alert v-if="!user.active_hpc_sub > 0" outlined type="error" color="error" class="my-2 px-2 py-1" dense>No active subscription...</v-alert>

                                    <v-btn v-if="selectedEvent.class_day_advance <= 14 && selectedEvent.class_capacity > 0 && !selectedEvent.class_booked && !selectedEvent.class_waitlist && user.active_hpc_sub > 0" color="primary lighten-1" class="px-2 py-1" small @click.stop="bookClass(selectedEvent.class_id, 'add')"><v-icon left small>event</v-icon>Book</v-btn>

                                    <v-btn class="px-2 py-1 mr-2" v-if="selectedEvent.class_day_advance <= 14 && selectedEvent.class_capacity < 1 && !selectedEvent.class_booked && !selectedEvent.class_waitlist && user.active_hpc_sub > 0" color="primary lighten-1" small outlined><v-icon left small>not_interested</v-icon>Sold Out</v-btn>
                                    <v-btn v-if="selectedEvent.class_capacity == 0 && !selectedEvent.class_booked && !selectedEvent.class_waitlist && user.active_hpc_sub > 0" color="info lighten-1" class="px-2 py-1" small @click.stop="bookClass(selectedEvent.class_id, 'waitlist')"><v-icon left small>event</v-icon>Wait List</v-btn>

                                    <v-btn v-if="selectedEvent.class_booked && !selectedEvent.class_waitlist && user.active_hpc_sub > 0" color="primary lighten-1" class="px-2 py-1" small @click.stop="bookClass(selectedEvent.class_id, 'remove')"><v-icon left small>delete</v-icon>Remove Booking</v-btn>

                                    <v-btn v-if="!selectedEvent.class_booked && selectedEvent.class_waitlist && user.active_hpc_sub > 0" color="primary lighten-2" class="px-2 py-1" small @click.stop="bookClass(selectedEvent.class_id, 'remove')"><v-icon left small>delete</v-icon>Remove Waitlist</v-btn>

                                    <v-alert v-model="classesError" dismissible dense outlined text type="error" class="mt-4 mb-0">{{ classesErrorMsg }}</v-alert>
                                </div>
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
import store from "./_store";
import moment from 'moment';

export default {
    name: "ClassesModule",
    components: {
        //
    },
    $_veeValidate: {
        validator: "new",
    },
    data: () => ({
        slotHeight: 50, // 1 hour = 50px
        startHour: 5,
        endHour: 23,
        selectedEvent: null,
        classes_list: [], // all events/classes from backend
        classesLoaded: false,
        classesError: false,
        classesErrorMsg: '',
        showPrev: false,
        showNext: true,
        weekDays: [], // will now hold 28 days instead of a single week
        allDays: [], // stores all days for navigation
        allDays: [], // stores all days for navigation
        currentWeekOffset: 0, // for desktop
        currentMobileDayIndex: 0, // for mobile sliding
        daysVisibleMobile: 2, // show 2 days at a time
        daysVisibleDesktop: 7, // show 7 days at a time
        maxWeeks: 4, // total weeks available (28 days)
        touchStartX: 0,
        touchEndX: 0,
    }),
    computed: {
        // classesLoading(){
        //     return this.$store.getters['classes/classStatus'] == 'loading';
        // },
        // classes(){
        //     return this.$store.getters['classes/classes'];
        // },
        isMobile() {
            return window.innerWidth <= 768;
        },
        user() {
            return this.$store.getters["user/getProfile"];
        },
        startOfWeek() {
            return this.$moment().startOf("day").add(this.currentWeekOffset, "week");
        },
        // weekDays() {
        //     return Array.from({ length: 7 }, (_, i) =>
        //         this.startOfWeek.clone().add(i, "days")
        //     );
        // },
        timeSlots() {
            const slots = [];

            for (let i = this.startHour; i <= this.endHour; i++) {
                slots.push(this.$moment({ hour: i }).format("HH:mm"));
            }

            return slots;
        },
        formatWeekRange() {
            const start = this.startOfWeek.format("MMM D");
            const end = this.startOfWeek.clone().add(6, "days").format("MMM D");

            return `${start} – ${end}`;
        },
        visibleDays() {
            if (this.isMobile) {
                return this.allDays.slice(this.currentMobileDayIndex,this.currentMobileDayIndex + this.daysVisibleMobile);
            } else {
                const start = this.currentWeekOffset * this.daysVisibleDesktop;
                return this.allDays.slice(start, start + this.daysVisibleDesktop);
            }
        },
    },
    methods: {
        generateDays() {
            const startDate = moment().startOf("day");

            this.allDays = [];

            for (let i = 0; i < this.maxWeeks * 7; i++) {
                this.allDays.push(startDate.clone().add(i, "days"));
            }
        },
        setInitialMobileIndex() {
            const today = this.$moment().format("YYYY-MM-DD");
            const idx = this.weekDays.findIndex(d => d.format("YYYY-MM-DD") === today);
            this.currentMobileDayIndex = idx >= 0 ? idx : 0;
        },
        changeWeek(offset) {
            if (this.isMobile) {
                // On mobile, move by daysVisibleMobile (2 days)
                const step = this.daysVisibleMobile * (offset < 0 ? -1 : 1);
                const newIndex = this.currentMobileDayIndex + step;

                if (newIndex >= 0 && newIndex <= this.allDays.length - this.daysVisibleMobile) {
                this.currentMobileDayIndex = newIndex;
                }

                if(newIndex > 0){
                    this.showPrev = true;
                } else {
                    this.showPrev = false;
                }

                if(newIndex > 26){
                    this.showNext = false;
                } else {
                    this.showNext = true;
                }

            } else {
                // desktop → jump 7 days at a time
                const newOffset = this.currentWeekOffset + offset;

                if (newOffset >= 0 && newOffset <= this.maxWeeks - 1) {
                    this.currentWeekOffset = newOffset;
                }

                if(newOffset > 0){
                    this.showPrev = true;
                } else {
                    this.showPrev = false;
                }

                if(newOffset > 2){
                    this.showNext = false;
                } else {
                    this.showNext = true;
                }
            }
        },
        onTouchStart(e) {
            this.touchStartX = e.changedTouches[0].screenX;
            this.touchStartY = e.changedTouches[0].screenY;
        },
        onTouchEnd(e) {
            this.touchEndX = e.changedTouches[0].screenX;
            this.touchEndY = e.changedTouches[0].screenY;
            this.handleSwipe();
        },
        handleSwipe() {
            const deltaX = this.touchEndX - this.touchStartX;
            const deltaY = this.touchEndY - this.touchStartY;

            const minSwipeDistance = 50; // min horizontal distance

            // Ignore if vertical movement is larger than horizontal → means it's a scroll
            if (Math.abs(deltaY) > Math.abs(deltaX)) return;

            // Ignore small swipes
            if (Math.abs(deltaX) < minSwipeDistance) return;

            if (deltaX < 0) {
                // swipe left → next 2 days
                this.changeWeek(this.daysVisibleMobile);
            } else {
                // swipe right → prev 2 days
                this.changeWeek(-this.daysVisibleMobile);
            }
        },
        getPositionedEvents(events) {
            if (!events || events.length === 0) return [];

            // Step 1: sort by start time
            const sortedEvents = [...events].sort((a, b) => {
                return this.getMinutes(a.class_starts_formatted) - this.getMinutes(b.class_starts_formatted);
            });

            const clusters = [];
            let currentCluster = [];

            let clusterEnd = 0;

            // Step 2: create clusters of overlapping events
            for (const event of sortedEvents) {
                const start = this.getMinutes(event.class_starts_formatted);
                const end = start + event.duration;

                if (start >= clusterEnd) {
                    // start new cluster
                    if (currentCluster.length > 0) clusters.push(currentCluster);
                    currentCluster = [event];
                    clusterEnd = end;
                } else {
                    currentCluster.push(event);
                    clusterEnd = Math.max(clusterEnd, end);
                }
            }

            if (currentCluster.length > 0) clusters.push(currentCluster);

            // Step 3: for each cluster, assign columns
            clusters.forEach(cluster => {
                const columns = [];

                cluster.forEach(event => {
                    const start = this.getMinutes(event.class_starts_formatted);
                    const end = start + event.duration;

                    // Find first column without conflict
                    let col = 0;
                    while (columns[col] && this.getMinutes(columns[col].class_starts_formatted) + columns[col].duration > start) {
                        col++;
                    }

                    event._column = col;
                    columns[col] = event;
                });

                // Set total columns for each event
                cluster.forEach(event => {
                    event._totalColumns = columns.length;
                });
            });

            return sortedEvents;
        },

        getMinutes(timeStr) {
            const [hour, min] = timeStr.split(":").map(Number);
            return hour * 60 + min;
        },
        getUniqueEvents(){
            const uniqueByEvents = _.uniqBy(this.classes_list, function(item) {
                return item.class_colour + '-' + item.class_activity;
            });

            return uniqueByEvents;
        },
        getEventsForDay(day) {
            const dateStr = day.format("YYYY-MM-DD");

            // const today = this.$moment().startOf("day");
            // const lastDay = today.clone().add(4, "weeks");

            // Skip events not in next 14 days
            // if (day.isBefore(today) || day.isAfter(lastDay)) return [];

            const dayEvents = this.classes_list.filter((event) => event.class_date_formatted === dateStr);

            return this.getPositionedEvents(dayEvents);
        },
        getEventStyle(event) {
            const start = this.getMinutes(event.class_starts_formatted);
            const top = ((start - this.startHour * 60) / 60) * this.slotHeight;

            const width = 100 / event._totalColumns;
            const left = width * event._column;
            const colour = event.class_colour;

            return {
                top: `${top + 2}px`,
                height: `${(event.duration / 60) * this.slotHeight - 4}px`,
                left: `${left + 1}%`,
                width: `calc(${width}% - 4px)`,
                'background-color': colour,
            };
        },
        getUniqueEventStyle(event) {
            const colour = event.class_colour;

            return {
                'background-color': colour,
            };
        },
        showEventDetails(event) {
            this.selectedEvent = event;
            this.classesError = false;
            this.classesErrorMsg = '';
        },
        bookClass(class_id, action){
            this.$store.dispatch("classes/bookRequest", {class_id: class_id, class_action: action, week: this.currentWeekOffset}).then((classes) => {
                this.selectedEvent = null;
                this.classes_list = classes.data;
                this.classesLoaded = true;
                this.classesError = false;
                this.classesErrorMsg = '';
            }).catch(err => {
                this.classesError = true;
                this.classesErrorMsg = err;
            });
        }
    },
    beforeCreate() {
        this.$store.dispatch('user/userRequest');

        this.$store.dispatch('classes/classesRequest', {week: 0}).then((classes) => {
            this.classes_list = classes.data;
            this.classesLoaded = true;
        });
    },
    created() {
        this.generateDays();
    },
    mounted() {
        if (this.isMobile) {
            const calendar = this.$refs.calendarContainer;
            window.addEventListener("touchstart", this.onTouchStart, { passive: true });
            window.addEventListener("touchend", this.onTouchEnd, { passive: true });
        }
    },
    beforeDestroy() {
        if (this.isMobile) {
            window.removeEventListener("touchstart", this.onTouchStart);
            window.removeEventListener("touchend", this.onTouchEnd);
        }
    },
};
</script>

<style scoped>
.cl-legend {
    border: 1px solid #000;
    padding: 8px 24px;
    margin-right: 10px;
    color: #ffffff;
    font-weight: bold;
    text-transform: capitalize;
}
.cl-booked {
    background-color: var(--primary-color);
}
.cl-waitlist {
    background-color: #3f51b5;
}
.cl-avail {
    background-color: #ff8200;
}
@media screen and (max-width: 767px) {
    .legend-wrapper {
        display: flex !important;
        flex-direction: column;
    }
    .legend-wrapper .cl-legend {
        width: 100%;
        margin: 0 !important;
    }
}

.week-view {
    width: 100%;
    margin: auto;
    display: block;
    position: relative;
}

.controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 10px 0;
}

.header-row {
    display: flex;
    background: #f0f0f0;
}

.time-column-header {
    width: 60px;
}

.day-column {
    flex: 1;
    text-align: center;
    border: 1px solid #ccc;
    padding: 5px;
}

.day-label {
    font-weight: bold;
    line-height: 16px;
}

.date-label {
    font-size: 0.9em;
    color: #555;
    line-height: 16px;
}

.time-row {
    display: flex;
}

.body-row {
    display: flex;
    flex-direction: row;
}

.time-column {
    width: 60px;
    text-align: center;
    padding-right: 0px;
    border: 1px solid #ccc;
    background: #f9f9f9;
}

.day-cell {
    flex: 1;
    position: relative;
    height: auto;
    border: 1px solid #eee;
    /* cursor: pointer; */
}

.event-layer {
    position: absolute;
    top: 78px;
    left: 60px;
    right: 0;
    bottom: 0;
    display: flex;
    pointer-events: none; /* let clicks pass through unless on event */
}

.day-events {
    flex: 1;
    position: relative;
    pointer-events: auto;
}

.event {
    position: absolute;
    left: 2px;
    right: 2px;
    background-color: var(--primary-color);
    color: white;
    font-size: 12px;
    padding: 4px 4px;
    border-radius: 4px;
    z-index: 2;
    cursor: pointer;
}

.event.event-booked {
    background-color: #ff8200 !important;
}

.event.event-waitlist {
    background-color: #3f51b5 !important;
}

.event:hover {
    background-color: var(--secondary-color);
    color: #000;
}

.event p {
    margin: 0;
    text-transform: capitalize;
    font-weight: 900;
    font-size: 14px;
    line-height: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Modal Styles */
.modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 4;
}

.modal-content {
    background: white;
    padding: 20px;
    max-width: 600px;
    width: 100%;
    border-radius: 8px;
    position: relative;
}

.close {
    position: absolute;
    top: 0px;
    right: 12px;
    font-size: 22px;
    cursor: pointer;
    font-weight: 900;
}

@media screen and (max-width: 767px) {
    .modal-content {
        max-width: calc(100% - 40px);
        max-height: calc(100vh - 120px);
        overflow-y: scroll;
    }

    .week-view {
        /* overflow-x: hidden; */

    }
    .controls {
        padding-top: 10px;
        padding-bottom: 10px;
        position: sticky;
        top: 0;
        z-index: 3;
        background: #ffffff;;
    }
    .header-row {
        position: sticky;
        top: 44px;
        z-index: 3;
        box-shadow: 0 2px 5px rgba(0,0,0,0.1);
    }
    .header-row,
    .time-row {
        display: grid;
        grid-template-columns: 60px repeat(2, 1fr); /* 2 days columns */
    }
    .day-cell {
        min-width: 120px;
    }
    .event {
        font-size: 0.75rem;
        padding: 1px 3px;
    }
}
</style>
